import { ChangeEvent, useContext, useRef, useState } from 'react'
import { Button, Header, LoadingSpinner, TextInput } from '@components/Generic'
import { observer } from 'mobx-react'
import axios from 'axios'
import { axiosInstance } from '@methods/axiosConfig'
import { forceRedirect } from '@methods/forceRedirect'
import { StoreContext } from '@store/Root.store'

import { devLog } from '@/methods/devLog'
import { cx } from '@/utils/cx'
import { format } from '@/methods/format'

// GoCardless `GET /accounts/{id}/transactions/` payload, pasted as-is. The backend validates the
// entries (`mockAccountTransactionsSchema` in attempt-api); the client only checks the shape and
// the size cap it shares with the backend before sending.
interface MockAccountTransactions {
  transactions: { booked: unknown[]; pending?: unknown[] }
  last_updated?: string | null
}

const MAX_MOCK_BOOKED_TRANSACTIONS = 300

type TransactionsValidation =
  | { status: 'empty' }
  | { status: 'valid'; value: MockAccountTransactions }
  | { status: 'invalid'; reason: 'invalidJson' | 'invalidShape' | 'tooMany' }

const validateAccountTransactions = (text: string): TransactionsValidation => {
  if (!text.trim()) return { status: 'empty' }

  let parsed: unknown

  try {
    parsed = JSON.parse(text)
  } catch {
    return { status: 'invalid', reason: 'invalidJson' }
  }

  const booked = (parsed as { transactions?: { booked?: unknown } } | null)?.transactions?.booked

  if (!parsed || typeof parsed !== 'object' || !Array.isArray(booked)) {
    return { status: 'invalid', reason: 'invalidShape' }
  }

  if (booked.length > MAX_MOCK_BOOKED_TRANSACTIONS) {
    return { status: 'invalid', reason: 'tooMany' }
  }

  return { status: 'valid', value: parsed as MockAccountTransactions }
}

export const GocardlessCustomTransactionDataPage = observer(() => {
  const store = useContext(StoreContext)
  const { gocardlessTransactionDataSelectionPage: trans } = store.TranslationsState.translations

  const [isLoading, setIsLoading] = useState(false)

  const [accountNumber, setAccountNumber] = useState('')
  const [bankCode, setBankCode] = useState('')
  const [bic, setBic] = useState('')
  const [holderName, setHolderName] = useState('')
  const [iban, setIban] = useState('')
  const [accountTransactionsJson, setAccountTransactionsJson] = useState('')
  const [serverError, setServerError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)

  const transactionsValidation = validateAccountTransactions(accountTransactionsJson)
  const transactionsError =
    transactionsValidation.status === 'invalid'
      ? {
          invalidJson: trans.accountTransactionsInvalidJson,
          invalidShape: trans.accountTransactionsInvalidShape,
          tooMany: format(trans.accountTransactionsTooMany, { max: MAX_MOCK_BOOKED_TRANSACTIONS }),
        }[transactionsValidation.reason]
      : undefined

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // let the same file be picked again after a fix
    event.target.value = ''

    if (!file) return

    try {
      setAccountTransactionsJson(await file.text())
      setServerError(null)
    } catch (error) {
      devLog('Error reading transactions file:', error)
    }
  }

  const handleProceed = async () => {
    if (transactionsValidation.status === 'invalid') return

    try {
      setIsLoading(true)
      setServerError(null)

      const { data } = await axiosInstance.post<{ redirectUrl: string }>(
        '/gocardless/set-mock-data',
        {
          accountNumber,
          bankCode,
          bic,
          holderName,
          iban,
          ...(transactionsValidation.status === 'valid'
            ? { accountTransactions: transactionsValidation.value }
            : {}),
        },
        {
          withCredentials: true,
        }
      )

      if (data.redirectUrl) {
        forceRedirect(data.redirectUrl)
      } else {
        throw new Error('No redirect URL returned from server')
      }
    } catch (error) {
      devLog('Error setting mock data:', error)

      // A 400 carries the reason, e.g. "Bank account transactions not requested" when the current
      // flow does not use transactions.
      const message =
        axios.isAxiosError(error) && error.response?.status === 400
          ? (error.response.data as { message?: string } | undefined)?.message
          : undefined

      setServerError(message || trans.setMockDataError || null)
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    store.ScenarioState.prevPage()
  }

  return (
    <div className="page-container w-500 p-40">
      <Header className="no-margin">{trans.customTransactionDataHeader}</Header>

      <div className="generic__input-container">
        <div className="generic__label">{trans.accountNumberLabel}</div>
        <TextInput
          type="text"
          value={accountNumber}
          onChange={(value) => {
            setAccountNumber(value)
          }}
          id="account-number-input"
          autoComplete="on"
          placeholder={trans.accountNumberPlaceholder}
          disabled={isLoading}
        />
      </div>

      <div className="generic__input-container">
        <div className="generic__label">{trans.bankCodeLabel}</div>
        <TextInput
          type="text"
          value={bankCode}
          onChange={(value) => {
            setBankCode(value)
          }}
          id="bank-code-input"
          autoComplete="on"
          placeholder={trans.bankCodePlaceholder}
          disabled={isLoading}
        />
      </div>

      <div className="generic__input-container">
        <div className="generic__label">{trans.bicLabel}</div>
        <TextInput
          type="text"
          value={bic}
          onChange={(value) => {
            setBic(value)
          }}
          id="bic-input"
          autoComplete="on"
          placeholder={trans.bicPlaceholder}
          disabled={isLoading}
        />
      </div>

      <div className="generic__input-container">
        <div className="generic__label">{trans.holderNameLabel}</div>
        <TextInput
          type="text"
          value={holderName}
          onChange={(value) => {
            setHolderName(value)
          }}
          id="holder-name-input"
          autoComplete="on"
          placeholder={trans.holderNamePlaceholder}
          disabled={isLoading}
        />
      </div>

      <div className="generic__input-container">
        <div className="generic__label">{trans.ibanLabel}</div>
        <TextInput
          type="text"
          value={iban}
          onChange={(value) => {
            setIban(value)
          }}
          id="iban-input"
          autoComplete="on"
          placeholder={trans.ibanPlaceholder}
          disabled={isLoading}
        />
      </div>

      <div className="generic__input-container">
        <label className="generic__label" htmlFor="account-transactions-input">
          {trans.accountTransactionsLabel}
        </label>
        <textarea
          id="account-transactions-input"
          className="text-input__input onboarding-text-input gocardless-tds__textarea"
          rows={8}
          value={accountTransactionsJson}
          onChange={(event) => {
            setAccountTransactionsJson(event.target.value)
            setServerError(null)
          }}
          placeholder={trans.accountTransactionsPlaceholder}
          disabled={isLoading}
          spellCheck={false}
          aria-invalid={!!transactionsError}
          aria-describedby={transactionsError ? 'account-transactions-error' : undefined}
        />

        <div className="gocardless-tds__file-row">
          <input
            ref={fileInputRef}
            className="gocardless-tds__file-input"
            type="file"
            accept="application/json,.json"
            onChange={(event) => void handleFile(event)}
            tabIndex={-1}
            aria-hidden="true"
          />
          <Button
            className="button--secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            dataTestid="account-transactions-file"
          >
            <span className="generic__button-text">{trans.accountTransactionsFileButton}</span>
          </Button>
          {accountTransactionsJson && (
            <Button
              className="button--link"
              onClick={() => setAccountTransactionsJson('')}
              disabled={isLoading}
              dataTestid="account-transactions-clear"
            >
              <span className="generic__button-text">{trans.accountTransactionsClear}</span>
            </Button>
          )}
        </div>

        {transactionsError && (
          <span
            id="account-transactions-error"
            className="text-input__error-msg onboarding-text-input-error"
            role="alert"
          >
            {transactionsError}
          </span>
        )}
      </div>

      {serverError && (
        <p className="generic__paragraph generic__error-message" role="alert">
          {serverError}
        </p>
      )}

      <div className="layout__content-container-bottom-fixed layout__content-container-bottom-fixed--no-border">
        <Button
          onClick={() => handleBack()}
          className="button--secondary button--w164"
          disabled={isLoading}
        >
          <span className="generic__button-text">{trans.backButton}</span>
        </Button>

        <Button
          onClick={() => !isLoading && handleProceed()}
          className="button--w164"
          disabled={!holderName || transactionsValidation.status === 'invalid'}
          dataTestid="set-mock-data-proceed"
        >
          {isLoading && <LoadingSpinner inButton />}
          <span
            className={cx('generic__button-text', isLoading && 'generic__button-text--loading')}
          >
            {trans.proceedButton}
          </span>
        </Button>
      </div>
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
